import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import cx from 'classnames'
import { StoreSnapshot, Editor, createTLStore } from '@tldraw/tldraw'

import Styles from './FullScreenPreviewWithCarouselV2.module.scss'

import { PostPreview } from './PostPreview'
import { FullScreenPreviewProps, TYPES_WITH_PREVIEW } from './Types'

import { Header } from './Header'
import { LoadingPreview } from './LoadingPreview/LoadingPreview'
import { carouselClasses } from './carouselClasses'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useIsScheduledPostsV3 } from 'hooks/isScheduledPostsV3'
import { Chat } from 'components/Chat'
import { useMarkFileAsViewed } from '../useMarkFileAsViewed'
import { Toolbar } from './Toolbar/Toolbar'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { MediaObject } from 'types'
import { shapeUtils } from 'components/VisualStudioPlayground/VisualEditor/VisualEditor'
import { isBlankDesign as checkIsBlankDesign } from 'components/Design/utils/isBlankDesign'
import { DesignChannel } from 'components/NewFileModal/Types'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useContentPreviewFiles } from 'hooks/calendar/useContentPreviewFiles'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { GenerationType } from 'components/BlazeCommandLine/api/createDocGenerationRequest'
import { ViewAsMenu } from './ViewAsMenu/ViewAsMenu'
import { ConditionalWrapper } from 'common/ConditionalWrapper/ConditionalWrapper'
import { useFullscreenBackNavigation } from './useFullscreenBackNavigation'
import { useOpenFullscreenPost } from '../WeekViewV2/useOpenLastFullscreenPost'
import { useDesignPreviewBroadcast } from './useDesignPreviewBroadcast'
import { ContentRating } from './ContentRating/ContentRating'
import { ChatComparisonProvider, useOptionalChatComparison } from './ChatComparisonContext'
import { CurrentSlideProvider } from './CurrentSlideContext'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { ChatComparison } from './ChatComparison/ChatComparison'
import { useNextGenChatMessageCost } from 'hooks/useNextGenChatMessageCost'
import { useAuth } from 'hooks/UseAuth'
import { PostStatusPropertyValue } from 'api/clients/PropertyClient'

type Direction = 'left' | 'right'

export const CROSSPOST_UNSUPPORTED_CHANNELS: string[] = [ChannelType.Newsletter, ChannelType.BlogPost]

// Pre-allocated style objects to avoid recreation on every render
const VISIBLE_SLIDE_STYLE = { zIndex: 1, opacity: 1, pointerEvents: 'auto' as const }
const SENTINEL_STYLE = { top: 20, left: 0, height: 1, width: '100%', position: 'absolute' as const }

export const FullScreenPreviewWithCarouselV2 = ({
  id,
  startDate,
  filters,
  onClose,
  channel,
  width,
  height,
  assetType,
  loadingStateColor,
  showCarousel = true,
  useChat = true,
}: FullScreenPreviewProps) => {
  const auth = useAuth()
  const { markFileAsViewed } = useMarkFileAsViewed()

  const {
    currentItem,
    displayedFile,
    handleRemoveCurrentFile,
    handleUpdateAlmanacFile,
    isFileFetching,
    isFileLoading,
    nextFile,
    nextKey,
    previousFile,
    previousKey,
    setCurrentItem,
  } = useContentPreviewFiles({
    id,
    startDate,
    filters,
    showCarousel,
    onClose,
  })

  const fileKey = displayedFile?.key || currentItem.id || id
  const filePostAt = displayedFile?.post_at || currentItem.startDate || startDate || ''

  useOpenFullscreenPost({
    fileKey,
    filePostAt,
    fileChannel: displayedFile?.channel,
  })
  const { useBackButtonListener, handleClose, handleBack } = useFullscreenBackNavigation({
    onClose,
    currentItemDate: displayedFile?.post_at || currentItem.startDate,
  })

  useBackButtonListener()

  // Subscribe to DesignPreviewChannel broadcasts to receive updates when generation completes
  useDesignPreviewBroadcast({
    designKey: displayedFile?.key,
    enabled: true,
  })

  const [isHeaderIntersecting, setIsHeaderIntersecting] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [platformsByFile, setPlatformsByFile] = useState<Record<string, ExternalIntegrationEnum | undefined>>({})
  const [isInsertingMediaObjects, setIsInsertingMediaObjects] = useState(false)
  const [seeMore, setSeeMore] = useState(false)

  const designerChatEnabled = useFlipperFlag('designer_chat_assistant')
  const scheduledPostTargetsEnabled = useIsScheduledPostsV3()
  const { showNextGenContentPreferences } = useShowNextGenContentPreferences()

  useEffect(() => {
    markFileAsViewed(fileKey)
  }, [fileKey, markFileAsViewed])

  const selectPlatformHandlersRef = useRef<Record<string, (platform: ExternalIntegrationEnum) => void>>({})
  const platformsByFileRef = useRef<Record<string, ExternalIntegrationEnum | undefined>>({})

  const previewRef = useRef<HTMLDivElement>(null)
  const postPreviewRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLDivElement>(null)
  const centerColumnRef = useRef<HTMLDivElement>(null)
  const topSentinelRef = useRef<HTMLDivElement>(null)

  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 })

  useLayoutEffect(() => {
    const node = previewRef.current
    if (!node) return

    const measure = () => {
      const rect = node.getBoundingClientRect()
      setPreviewSize({ width: rect.width, height: rect.height })
    }

    measure()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => measure())
      observer.observe(node)
      return () => observer.disconnect()
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure)
      return () => window.removeEventListener('resize', measure)
    }

    return undefined
  }, [displayedFile?.key])

  const elementWidth = previewSize.width
  const elementHeight = previewSize.height

  const showPostPreview = displayedFile?.channel && TYPES_WITH_PREVIEW.includes(displayedFile.channel)

  const currentItemIndex = useMemo(() => (previousFile ? 1 : 0), [previousFile])

  const hasRatingOverlay =
    !!displayedFile?.completed_ai_suggestion_request?.id && !displayedFile.completed_ai_suggestion_request.rated

  // Get classes for the current (center) item only
  const currentItemClasses = useMemo(() => {
    if (!displayedFile) return {}
    // Use displayedFile as fallback for next/previous if they're null (only affects non-current items)
    return carouselClasses(
      displayedFile,
      nextFile || displayedFile,
      previousFile || displayedFile,
      showCarousel,
      currentItemIndex,
      elementHeight,
      elementWidth,
      !!(scheduledPostTargetsEnabled && showPostPreview),
      true,
      hasRatingOverlay,
    ).getCarouselItemClassNames(currentItemIndex)
  }, [
    displayedFile,
    nextFile,
    previousFile,
    currentItemIndex,
    elementHeight,
    elementWidth,
    scheduledPostTargetsEnabled,
    showPostPreview,
    showCarousel,
    hasRatingOverlay,
  ])

  const startNavigation = useCallback(
    (direction: Direction) => {
      // Force synchronous update - bypasses React 18's automatic batching
      flushSync(() => {
        if (direction === 'right' && nextKey) {
          setCurrentItem({ id: nextKey, startDate: nextFile?.post_at ?? null })
        } else if (direction === 'left' && previousKey) {
          setCurrentItem({ id: previousKey, startDate: previousFile?.post_at ?? null })
        }
      })
    },
    [nextKey, nextFile?.post_at, previousKey, previousFile?.post_at, setCurrentItem],
  )

  const handleLeftClick = useCallback(() => startNavigation('left'), [startNavigation])
  const handleRightClick = useCallback(() => startNavigation('right'), [startNavigation])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      if (
        target.closest('[role="dialog"]') ||
        target.closest('textarea') ||
        target.closest('input') ||
        target.closest('[contenteditable="true"]')
      ) {
        return
      }

      if (e.key === 'Escape') handleBack()
      else if (e.key === 'ArrowLeft') startNavigation('left')
      else if (e.key === 'ArrowRight') startNavigation('right')
    },
    [handleBack, startNavigation],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  useLayoutEffect(() => {
    const rootEl = centerColumnRef.current
    const targetEl = topSentinelRef.current
    if (!rootEl || !targetEl) return

    setIsHeaderIntersecting(rootEl.scrollTop > 0)

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        setIsHeaderIntersecting(!entry.isIntersecting)
      },
      {
        root: rootEl,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      },
    )

    observer.observe(targetEl)

    return () => {
      observer.disconnect()
    }
  }, [displayedFile?.key])

  // Check if design is blank - create editor, check, then dispose immediately to free memory
  const isDesignBlank = useMemo(() => {
    if (!displayedFile?.design_content) return false
    const store = createTLStore({ shapeUtils })
    store.loadSnapshot(displayedFile.design_content as StoreSnapshot<any>)
    const editor = new Editor({
      store,
      shapeUtils: shapeUtils as any,
      tools: [],
      getContainer: () => document.createElement('div'),
    })
    const isBlank = checkIsBlankDesign(editor)
    // Dispose the editor immediately to prevent memory leaks
    editor.dispose()
    return isBlank
  }, [displayedFile?.design_content])

  // Sync state to ref for stable callback
  platformsByFileRef.current = platformsByFile

  const getCurrentPreviewPlatform = useCallback((fileKey: string) => platformsByFileRef.current[fileKey], [])
  const setPreviewPlatformForFile = useCallback((fileKey: string, platform: ExternalIntegrationEnum) => {
    setPlatformsByFile((prev) => {
      if (prev[fileKey] === platform) return prev
      return { ...prev, [fileKey]: platform }
    })
  }, [])

  const getSelectPlatformHandler = useCallback(
    (fileKey: string) => {
      if (!selectPlatformHandlersRef.current[fileKey]) {
        selectPlatformHandlersRef.current[fileKey] = (platform: ExternalIntegrationEnum) =>
          setPreviewPlatformForFile(fileKey, platform)
      }
      return selectPlatformHandlersRef.current[fileKey]
    },
    [setPreviewPlatformForFile],
  )

  const toolbarPostWithMediaObjects = useMemo(() => {
    if (!displayedFile) return undefined
    if (displayedFile?.scheduled_post?.media_objects.length === 0 && displayedFile?.image_url) {
      return {
        ...displayedFile?.scheduled_post,
        media_objects: [
          {
            id: 1,
            source_url: displayedFile?.image_url,
            thumbnail_url: displayedFile?.image_url,
            mime_type: 'image/png',
            metadata: { width: displayedFile?.width, height: displayedFile?.height },
            design_key: displayedFile?.key,
            position: 0,
          } as MediaObject,
        ],
      } as ScheduledPostShow
    }
    return displayedFile?.scheduled_post
  }, [displayedFile])

  const regenerationCreditCost = useNextGenChatMessageCost(displayedFile)

  const shouldShowFileLoadingState = !displayedFile && (isFileLoading || isFileFetching)

  // Create stable delete handler that doesn't change
  const getDeleteHandler = useCallback(
    (fileKey: string) => () => handleRemoveCurrentFile(fileKey),
    [handleRemoveCurrentFile],
  )

  const isCurrentChannelUnsupported = CROSSPOST_UNSUPPORTED_CHANNELS.includes(displayedFile?.channel as string)

  // Render the current slide only
  const currentFilePreviewPlatform = displayedFile?.key ? getCurrentPreviewPlatform(displayedFile.key) : undefined

  const renderSlide = useCallback(
    (file: AlmanacFileShowItem) => {
      const deleteHandler = getDeleteHandler(file.key)
      const viewAsMenuEnabled = channel && !isCurrentChannelUnsupported

      return (
        <div key={file.key} className={Styles.slide} ref={previewRef} data-role="current" style={VISIBLE_SLIDE_STYLE}>
          <div
            className={cx(Styles.carouselItem, currentItemClasses, {
              [Styles.overflowing]: !!seeMore,
            })}
          >
            <div id="preview-sentinel" ref={topSentinelRef} style={SENTINEL_STYLE} />
            <ConditionalWrapper
              condition={viewAsMenuEnabled}
              wrapper={(children) => (
                <div className={cx({ [Styles.previewContentWrapper]: viewAsMenuEnabled })}>
                  <ViewAsMenu
                    activePlatform={currentFilePreviewPlatform}
                    onSelectPlatform={(platform: ExternalIntegrationEnum) => {
                      const platformHandler = getSelectPlatformHandler(file.key)
                      platformHandler(platform)
                      setSeeMore(false)
                    }}
                    fileChannel={(displayedFile?.channel || channel) as ChannelType | DesignChannel | GenerationType}
                  />
                  {children}
                </div>
              )}
            >
              <PostPreview
                ref={postPreviewRef}
                almanacFile={file}
                isLoading={shouldShowFileLoadingState}
                showVideoControls
                isActive={true}
                setIsLoading={setIsLoading}
                onClose={handleClose}
                onDelete={deleteHandler}
                filters={filters}
                fileChannel={channel}
                startDate={startDate || undefined}
                previewPlatform={currentFilePreviewPlatform}
                onSelectPlatform={getSelectPlatformHandler(file.key)}
                isInsertingMedia={isInsertingMediaObjects}
                isTransitioning={false}
                showHoverActionOverlay
                onSeeMoreToggle={(expanded: boolean) => setSeeMore(expanded)}
                seeMore={seeMore}
                hasRatingOverlay={hasRatingOverlay}
              />
            </ConditionalWrapper>
          </div>
        </div>
      )
    },
    [
      getDeleteHandler,
      channel,
      isCurrentChannelUnsupported,
      currentItemClasses,
      seeMore,
      shouldShowFileLoadingState,
      setIsLoading,
      handleClose,
      filters,
      startDate,
      currentFilePreviewPlatform,
      getSelectPlatformHandler,
      isInsertingMediaObjects,
      displayedFile?.channel,
      hasRatingOverlay,
    ],
  )

  if (shouldShowFileLoadingState)
    return (
      <div className={Styles.fullScreenPreview}>
        <div className={Styles.loading} data-testid="loading-preview">
          <LoadingPreview
            channel={channel}
            onClose={handleBack}
            width={width}
            height={height}
            assetType={assetType}
            color={loadingStateColor}
          />
        </div>
      </div>
    )

  if (!displayedFile)
    return (
      <div className={Styles.fullScreenPreview}>
        <div className={Styles.loading} data-testid="loading-preview">
          <LoadingPreview
            channel={channel}
            onClose={handleBack}
            width={width}
            height={height}
            assetType={assetType}
            color={loadingStateColor}
          />
        </div>
      </div>
    )

  const isPosted = displayedFile?.post_status === PostStatusPropertyValue.Posted
  const showChat = designerChatEnabled && useChat && displayedFile?.asset_type === 'Design' && !!auth && !isPosted
  const showComparison = showNextGenContentPreferences && showChat

  return (
    <div className={Styles.fullScreenPreview}>
      <Header
        id={currentItem.id}
        startDate={currentItem.startDate}
        channel={channel ?? undefined}
        almanacFile={displayedFile || undefined}
        isIntersecting={isHeaderIntersecting}
        onClose={handleBack}
        ref={headerRef}
        filters={filters}
        isLoading={isLoading || shouldShowFileLoadingState}
        setIsLoading={setIsLoading}
        onDelete={() => handleRemoveCurrentFile(displayedFile?.key)}
        chatOpen={showChat}
      />

      <ConditionalWrapper
        condition={showComparison}
        wrapper={(children) => <ChatComparisonProvider>{children}</ChatComparisonProvider>}
      >
        <div
          className={Styles.body}
          data-chat-open={showChat}
          data-nextgen-chat={showNextGenContentPreferences ? 'on' : undefined}
        >
          <div className={Styles.leftColumn}>
            <div
              className={cx(Styles.chatContainer, { [Styles.isVisible]: showChat })}
              data-chat-animation="idle"
              data-current-visible={showChat}
              data-show-chat={showChat}
              data-nextgen-chat={showNextGenContentPreferences ? 'on' : undefined}
            >
              {showChat && displayedFile?.key && (
                <Chat
                  isOpen
                  fileId={displayedFile.key}
                  context={{ design_id: displayedFile.key }}
                  isNextGen={showNextGenContentPreferences}
                  regenerationCreditCost={regenerationCreditCost}
                />
              )}
            </div>
          </div>

          <CurrentSlideProvider activePlatform={currentFilePreviewPlatform}>
            <div className={cx(Styles.centerColumn, { [Styles.isUnauthedUser]: !auth })} ref={centerColumnRef}>
              <ComparisonOrChildren displayedFile={displayedFile}>
                <div
                  className={cx(Styles.carousel, {
                    [Styles.isArticlePreview]: displayedFile?.asset_type === 'Article',
                  })}
                  data-phase="idle"
                  data-direction="right"
                  role="region"
                  aria-roledescription="carousel"
                >
                  {renderSlide(displayedFile)}
                </div>

                {displayedFile?.completed_ai_suggestion_request?.id && (
                  <div className={Styles.contentRating}>
                    <ContentRating
                      subjectId={displayedFile.completed_ai_suggestion_request.id}
                      fileKey={displayedFile.key}
                      initialRated={displayedFile.completed_ai_suggestion_request.rated}
                    />
                  </div>
                )}
              </ComparisonOrChildren>
            </div>
          </CurrentSlideProvider>

          <div className={Styles.rightColumn}>
            <RightColumnContent>
              {displayedFile && channel && !!auth && (
                <Toolbar
                  scheduledPost={toolbarPostWithMediaObjects as ScheduledPostShow}
                  almanacFile={displayedFile}
                  previewPlatform={getCurrentPreviewPlatform(displayedFile.key)}
                  onSelectPlatform={getSelectPlatformHandler(displayedFile.key)}
                  isBlankDesign={isDesignBlank}
                  onLoadingChange={setIsInsertingMediaObjects}
                  isLoading={isLoading}
                  isInsertingMedia={isInsertingMediaObjects}
                  setIsLoading={setIsLoading}
                  onClose={handleClose}
                  onDelete={(skipNavigation) => handleRemoveCurrentFile(displayedFile.key, skipNavigation)}
                  filters={filters}
                  fileChannel={displayedFile?.channel || channel}
                  startDate={startDate || undefined}
                  onUpdateAlmanacFile={handleUpdateAlmanacFile}
                  isCurrentChannelUnsupported={isCurrentChannelUnsupported}
                  showNavigation={showCarousel}
                  goToPreviousFile={previousFile ? handleLeftClick : undefined}
                  goToNextFile={nextFile ? handleRightClick : undefined}
                />
              )}
            </RightColumnContent>
          </div>
        </div>
      </ConditionalWrapper>
    </div>
  )
}

const ComparisonOrChildren = ({
  children,
  displayedFile,
}: {
  children: React.ReactNode
  displayedFile: AlmanacFileShowItem
}) => {
  const comparisonContext = useOptionalChatComparison()

  if (!!comparisonContext?.comparisons?.length && !comparisonContext?.isDismissed) {
    return <ChatComparison displayedFile={displayedFile} />
  }

  return <>{children}</>
}

export const RightColumnContent = ({ children }: { children: React.ReactNode }) => {
  const comparisonContext = useOptionalChatComparison()
  const isComparing = !!comparisonContext?.comparisons?.length && !comparisonContext?.isDismissed

  if (isComparing) return null
  return <>{children}</>
}
